import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { API, useAuth } from "../App";
import { ProfileBadge, ProfileBadgeStrip } from "../components/ProfileBadge";
import {
  Code, Award, Monitor, MapPin, Building2, Users, Download, LogIn, Eye
} from "lucide-react";

const COLORS = [
  "#06B6D4", "#A855F7", "#84CC16", "#F59E0B", "#EC4899",
  "#3B82F6", "#10B981", "#EF4444", "#8B5CF6", "#F97316",
  "#14B8A6", "#E879F9", "#22D3EE", "#FBBF24", "#34D399",
];

const BarChart = ({ data, maxCount }) => {
  const entries = Object.entries(data);
  if (!entries.length) return <p className="text-slate-500 text-sm py-4">No data available</p>;
  const max = maxCount || Math.max(...entries.map(([, v]) => v));

  return (
    <div className="space-y-2.5">
      {entries.map(([label, count], i) => (
        <div key={label} className="flex items-center gap-3 group">
          <span className="text-sm text-slate-600 w-48 truncate text-right shrink-0" title={label}>
            {label}
          </span>
          <div className="flex-1 h-7 bg-slate-100 rounded overflow-hidden">
            <div
              className="h-full rounded transition-all duration-700 ease-out flex items-center px-2"
              style={{
                width: `${Math.max((count / max) * 100, 2)}%`,
                backgroundColor: COLORS[i % COLORS.length],
              }}
            />
          </div>
          <span className="text-sm font-semibold text-slate-900 w-10 text-right">{count}</span>
        </div>
      ))}
    </div>
  );
};

const RankedList = ({ data, label }) => {
  const entries = Object.entries(data).slice(0, 15);
  if (!entries.length) return null;
  return (
    <Card className="card-surface">
      <CardContent className="pt-5">
        <h3 className="text-sm font-semibold text-brand-navy uppercase tracking-wider mb-3">Top {label}</h3>
        <div className="space-y-1.5">
          {entries.map(([name, count], i) => (
            <div key={name} className="flex items-center justify-between py-1 px-2 rounded hover:bg-slate-50">
              <span className="text-sm text-slate-600 truncate pr-2">{name}</span>
              <span className="text-sm font-bold text-slate-900">{count}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const ProfileCard = ({ profile }) => {
  const location = [profile.city, profile.state, profile.country].filter(Boolean).join(", ");

  return (
    <Card className="card-surface card-hover overflow-hidden" data-testid={`profile-card-${profile.profile_id}`}>
      <ProfileBadgeStrip type={profile.profile_type} />
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-slate-900 font-semibold italic opacity-60">Anonymous</h3>
          <ProfileBadge type={profile.profile_type} compact />
        </div>
        {location && (
          <p className="text-slate-500 text-sm flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />{location}
          </p>
        )}
        {profile.experience_years && (
          <p className="text-slate-500 text-sm">{profile.experience_years} yrs experience</p>
        )}
        {profile.skills?.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Skills</p>
            <div className="flex flex-wrap gap-1.5">
              {profile.skills.slice(0, 5).map((s, i) => (
                <span key={i} className="px-2 py-0.5 text-xs rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/20">
                  {s}
                </span>
              ))}
              {profile.skills.length > 5 && (
                <span className="px-2 py-0.5 text-xs rounded bg-slate-50 text-slate-500">
                  +{profile.skills.length - 5}
                </span>
              )}
            </div>
          </div>
        )}
        {profile.certifications?.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Certifications</p>
            <div className="flex flex-wrap gap-1.5">
              {profile.certifications.slice(0, 3).map((c, i) => (
                <span key={i} className="px-2 py-0.5 text-xs rounded bg-purple-500/15 text-purple-300 border border-purple-500/20">
                  {c}
                </span>
              ))}
              {profile.certifications.length > 3 && (
                <span className="px-2 py-0.5 text-xs rounded bg-slate-50 text-slate-500">
                  +{profile.certifications.length - 3}
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TAB_CONFIG = [
  { value: "skills", label: "Skills", icon: Code, dataKey: "skill_counts" },
  { value: "certifications", label: "Certifications", icon: Award, dataKey: "certification_counts" },
  { value: "software", label: "Software", icon: Monitor, dataKey: "software_counts" },
  { value: "locations", label: "Locations", icon: MapPin, dataKey: "location_counts" },
  { value: "affiliation", label: "Affiliation", icon: Building2, dataKey: "affiliation_counts" },
];

const SkillsMatrix = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visibleProfiles, setVisibleProfiles] = useState(20);
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/public/skills-matrix`);
        if (res.ok) setData(await res.json());
      } catch (err) {
        console.error("Failed to load skills matrix:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-app">
        <Header />
        <div className="flex items-center justify-center py-32"><div className="spinner" /></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-surface-app">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <p className="text-slate-500">Failed to load skills matrix data.</p>
        </div>
      </div>
    );
  }

  const profiles = data.profiles || [];

  return (
    <div className="min-h-screen bg-surface-app">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Public View Notice */}
        {!user && (
          <div className="bg-brand-gold/10 border border-[#FFD507]/30 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="public-notice">
            <Eye className="w-5 h-5 text-brand-navy mt-0.5 shrink-0" />
            <div>
              <p className="text-slate-900 font-medium">Public View</p>
              <p className="text-slate-500 text-sm">
                You're viewing the anonymous skills matrix. Personal information is hidden.{" "}
                <Link to="/" className="text-brand-navy hover:underline">Sign in</Link> to access full features.
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900" data-testid="skills-matrix-title">
              Skills Matrix
            </h1>
            <p className="text-slate-500 mt-1">
              Overview of skills and certifications across {data.total_profiles} profiles
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-slate-500">
              <Users className="w-5 h-5" />
              <span className="text-2xl font-bold text-slate-900">{data.total_profiles}</span>
              <span className="text-sm">profiles</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="skills" className="space-y-6" data-testid="matrix-tabs">
          <TabsList className="bg-surface-muted border border-slate-200">
            {TAB_CONFIG.map(tab => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="data-[state=active]:bg-brand-navy data-[state=active]:text-white"
                  data-testid={`tab-${tab.value}`}
                >
                  <Icon className="w-4 h-4 mr-2" />{tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {TAB_CONFIG.map(tab => (
            <TabsContent key={tab.value} value={tab.value}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <Card className="card-surface">
                    <CardContent className="pt-5">
                      <h3 className="text-slate-900 font-semibold mb-4 flex items-center gap-2">
                        <tab.icon className="w-4 h-4 text-brand-navy" />
                        {tab.label} Distribution
                      </h3>
                      <BarChart data={data[tab.dataKey] || {}} />
                    </CardContent>
                  </Card>
                </div>
                <RankedList data={data[tab.dataKey] || {}} label={tab.label} />
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Anonymous Profiles */}
        <div className="mt-12">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-1" data-testid="profiles-heading">
            Anonymous Profiles
          </h2>
          <p className="text-slate-500 text-sm mb-6">
            Personal information is hidden for privacy. Only location and skills are visible.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {profiles.slice(0, visibleProfiles).map((profile, i) => (
              <ProfileCard key={profile.profile_id || i} profile={profile} />
            ))}
          </div>

          {visibleProfiles < profiles.length && (
            <div className="text-center mt-8">
              <Button
                className="btn-outline"
                onClick={() => setVisibleProfiles(prev => prev + 20)}
                data-testid="load-more-profiles"
              >
                Load More ({profiles.length - visibleProfiles} remaining)
              </Button>
            </div>
          )}

          {/* CTA for non-logged-in users */}
          {!user && (
            <div className="mt-12 text-center py-10 bg-gradient-to-r from-[#0A0F1C] to-[#111424] rounded-xl border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Want to see more details?</h3>
              <p className="text-slate-500 mb-6">
                Sign in to access the full talent database with detailed profiles and advanced search.
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/">
                  <Button className="btn-primary"><LogIn className="w-4 h-4 mr-2" />Sign In</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SkillsMatrix;
